import type { Language, TranslationSchema } from '@/types'
import { v7 as uuid } from 'uuid'

import {
  BUSINESS_LANGUAGES,
  LEGACY_BASE_LANGUAGE,
} from '@/utils/languagePolicy'
import type { SupportedKeys } from './property.type'

export type PropertyTranslationFormValue = {
  id?: string
  lang: {
    label: Language
    value: Language
  }
  label: string
}

export type PropertyTranslationMutationInput = {
  id?: string
  lang: Language
  label: string
  fieldTranslationsId?: string
}

export type PropertyTranslationMutationPlan = {
  deletions: string[]
  upserts: Array<{
    input: PropertyTranslationMutationInput & { id: string }
    upsertType: 'create' | 'update'
  }>
}

const SUPPORTED_PROPERTY_LANGUAGES = [...BUSINESS_LANGUAGES] as Language[]

const isPropertyLanguage = (value: string): value is Language =>
  SUPPORTED_PROPERTY_LANGUAGES.includes(value as Language)

export const normalizePropertyTranslationLanguage = (
  value: string | undefined,
): Language | undefined => {
  if (!value) return undefined

  const normalized = value.toUpperCase()
  return isPropertyLanguage(normalized) ? normalized : undefined
}

const normalizePropertyTranslation = (
  translation: TranslationSchema,
): TranslationSchema | undefined => {
  const lang = normalizePropertyTranslationLanguage(translation.lang)
  if (!lang) return undefined

  return {
    ...translation,
    lang,
  }
}

const getTranslationTimestamp = (translation: TranslationSchema): number => {
  const rawTimestamp = translation.updatedAt ?? translation.createdAt
  if (!rawTimestamp) return Number.NaN

  const parsedTimestamp = Date.parse(rawTimestamp)
  return Number.isFinite(parsedTimestamp) ? parsedTimestamp : Number.NaN
}

const shouldReplaceCanonicalTranslation = (
  current: TranslationSchema | undefined,
  candidate: TranslationSchema,
): boolean => {
  if (!current) return true

  const currentTimestamp = getTranslationTimestamp(current)
  const candidateTimestamp = getTranslationTimestamp(candidate)

  if (
    Number.isFinite(candidateTimestamp) &&
    Number.isFinite(currentTimestamp)
  ) {
    return candidateTimestamp >= currentTimestamp
  }

  if (Number.isFinite(candidateTimestamp)) return true
  if (Number.isFinite(currentTimestamp)) return false

  // Fall back to "last writer wins" when no timestamp is available.
  return true
}

export const normalizePropertyTranslationLanguages = (
  languages: readonly string[] | undefined,
): Language[] => {
  const selected = new Set(
    (languages ?? [])
      .map((language) => language.toUpperCase())
      .filter(isPropertyLanguage),
  )

  return SUPPORTED_PROPERTY_LANGUAGES.filter((language) =>
    selected.has(language),
  )
}

export const normalizeEditablePropertyTranslationLanguages = (
  languages: readonly string[] | undefined,
): Language[] =>
  normalizePropertyTranslationLanguages(languages).filter(
    (language) => language !== LEGACY_BASE_LANGUAGE,
  )

export const indexPropertyTranslationsByLang = (
  translations: TranslationSchema[] | undefined,
): Partial<Record<Language, TranslationSchema>> => {
  if (!translations?.length) return {}

  return translations.reduce<Partial<Record<Language, TranslationSchema>>>(
    (acc, translation) => {
      const normalizedTranslation = normalizePropertyTranslation(translation)

      if (
        normalizedTranslation &&
        shouldReplaceCanonicalTranslation(
          acc[normalizedTranslation.lang],
          normalizedTranslation,
        )
      ) {
        acc[normalizedTranslation.lang] = normalizedTranslation
      }
      return acc
    },
    {},
  )
}

export const groupPropertyTranslationsByLang = (
  translations: TranslationSchema[] | undefined,
): Partial<Record<Language, TranslationSchema[]>> => {
  if (!translations?.length) return {}

  return translations.reduce<Partial<Record<Language, TranslationSchema[]>>>(
    (acc, translation) => {
      const normalizedTranslation = normalizePropertyTranslation(translation)
      if (!normalizedTranslation) return acc

      const currentTranslations = acc[normalizedTranslation.lang] ?? []
      acc[normalizedTranslation.lang] = [
        ...currentTranslations,
        normalizedTranslation,
      ]
      return acc
    },
    {},
  )
}

export const getPropertyTranslationsByLang = (
  field: Pick<SupportedKeys, 'translations'> &
    Partial<Record<Language, TranslationSchema>>,
): Partial<Record<Language, TranslationSchema>> => {
  const indexed = indexPropertyTranslationsByLang(field.translations?.items)

  for (const language of SUPPORTED_PROPERTY_LANGUAGES) {
    const existing = field[language]
    if (existing) indexed[language] = existing
  }

  return indexed
}

export const toPropertyTranslationFormValues = (
  field:
    | (Pick<SupportedKeys, 'translations'> &
        Partial<Record<Language, TranslationSchema>>)
    | null
    | undefined,
  visibleLanguages: readonly Language[],
): PropertyTranslationFormValue[] => {
  if (!field || visibleLanguages.length === 0) return []

  const translationsByLang = getPropertyTranslationsByLang(field)

  return visibleLanguages.reduce<PropertyTranslationFormValue[]>(
    (acc, language) => {
      const translation = translationsByLang[language]
      if (!translation) return acc

      acc.push({
        id: translation.id,
        lang: { label: language, value: language },
        label: translation.label,
      })

      return acc
    },
    [],
  )
}

export const toPropertyTranslationInputs = (
  translations: readonly PropertyTranslationFormValue[] | undefined,
  fieldTranslationsId: string,
): PropertyTranslationMutationInput[] => {
  if (!translations?.length) return []

  const translationsByLang = new Map<
    Language,
    PropertyTranslationMutationInput
  >()

  translations.forEach((translation) => {
    const lang = normalizePropertyTranslationLanguage(translation.lang.value)
    if (!lang || lang === LEGACY_BASE_LANGUAGE) return

    const label = translation.label.trim()
    if (!label) return

    translationsByLang.set(lang, {
      id: translation.id,
      lang,
      label,
      fieldTranslationsId,
    })
  })

  return Array.from(translationsByLang.values())
}

export const planPropertyTranslationMutations = ({
  currentTranslations,
  nextTranslations,
  fieldTranslationsId,
}: {
  currentTranslations: TranslationSchema[] | undefined
  nextTranslations: readonly PropertyTranslationFormValue[] | undefined
  fieldTranslationsId: string
}): PropertyTranslationMutationPlan => {
  const groupedCurrentTranslations =
    groupPropertyTranslationsByLang(currentTranslations)
  const canonicalCurrentTranslations =
    indexPropertyTranslationsByLang(currentTranslations)
  const desiredTranslations = toPropertyTranslationInputs(
    nextTranslations,
    fieldTranslationsId,
  )
  const desiredLanguages = new Set(desiredTranslations.map((t) => t.lang))

  const upserts = desiredTranslations.map((translation) => {
    const existingTranslation = canonicalCurrentTranslations[translation.lang]
    const translationId = existingTranslation?.id ?? uuid()

    return {
      input: {
        ...translation,
        id: translationId,
      },
      upsertType: existingTranslation?.id ? 'update' : 'create',
    } as const
  })

  const deletions = Object.entries(groupedCurrentTranslations).flatMap(
    ([rawLang, translationsForLang]) => {
      const lang = normalizePropertyTranslationLanguage(rawLang)
      if (!lang || lang === LEGACY_BASE_LANGUAGE) return []

      if (!desiredLanguages.has(lang)) {
        return translationsForLang.map((translation) => translation.id)
      }

      const canonicalTranslation = canonicalCurrentTranslations[lang]
      return translationsForLang
        .filter((translation) => translation.id !== canonicalTranslation?.id)
        .map((translation) => translation.id)
    },
  )

  return { deletions, upserts }
}
